/* ==============================================================================
   Malha WebRTC — o núcleo do produto
   ==============================================================================
   Uma `RTCPeerConnection` por par. Com N pessoas na sala, cada cliente mantém
   N−1 conexões e envia sua mídia N−1 vezes.

   Por que malha completa, e o custo que ela impõe
   ===============================================
   A mídia nunca passa pelo servidor. Isso é o requisito, e a consequência é que
   cada participante carrega o custo de upload de todos os outros:

     8 pessoas · 1 áudio                     ->  7 fluxos de áudio de upload
     8 pessoas · 1 áudio + 2 telas           ->  7 áudio + 14 vídeo = 21 fluxos

   É por isso que existe o governador de banda, e é por isso que o limite de 8 é
   honesto e visível em vez de uma degradação silenciosa.

   Negociação perfeita (perfect negotiation)
   =========================================
   Dois pares podem tentar oferecer ao mesmo tempo — "glare". Sem regra, os dois
   ficam em `have-local-offer` e a negociação trava para sempre. A regra:

     polite    cede. Se recebe offer enquanto tem uma pendente, faz rollback.
     impolite  ignora offer que colida com a própria.

   Quem é polite sai de `isPolitePeer(minhaSessão, sessãoDoPar)` — comparação
   lexicográfica que os DOIS lados calculam localmente e chegam ao mesmo
   resultado, sem round-trip. A função vive em `@concord/shared` para servidor e
   cliente nunca divergirem: divergência produz os dois polite (ninguém oferece)
   ou os dois impolite (deadlock).

   O DataChannel de controle
   =========================
   Cada par abre um `RTCDataChannel` chamado `ctrl`. Ele é o que faz a chamada
   sobreviver à queda do servidor: mute, mudança de tela e pedido de renegociação
   passam por ali quando o gateway não está disponível. É a razão pela qual
   "derrubar o servidor não derruba a chamada" é uma afirmação verdadeira e não
   marketing.
   ============================================================================== */

import { isPolitePeer } from '@concord/shared';

/** O que o motor precisa do gateway. Interface mínima, para poder ser testado. */
export interface SignalTransport {
  signal: (
    toUserId: string,
    toSessionId: string,
    payload:
      | { kind: 'offer'; sdp: string }
      | { kind: 'answer'; sdp: string }
      | {
          kind: 'ice';
          candidate: string;
          sdp_mid: string | null;
          sdp_m_line_index: number | null;
          username_fragment?: string | null;
        }
      | { kind: 'ice_complete' }
      | { kind: 'restart' },
  ) => boolean;
}

export type TrackPurpose = 'microphone' | 'camera' | 'screen';

export interface LocalTrack {
  track: MediaStreamTrack;
  purpose: TrackPurpose;
  /** Identificador estável da fonte, para o par remoto saber qual tela é qual. */
  sourceId: string;
  /** Rótulo humano ("Tela 1", "Chrome — documento"). */
  label: string;
}

export interface RemoteTrack {
  track: MediaStreamTrack;
  stream: MediaStream;
  purpose: TrackPurpose;
  peerUserId: string;
  peerSessionId: string;
  /** `mid` do transceiver, que é o que liga o track ao propósito anunciado. */
  mid: string | null;
}

export interface PeerState {
  userId: string;
  sessionId: string;
  polite: boolean;
  connection: RTCPeerConnection;
  control: RTCDataChannel | null;
  /** `true` entre `setLocalDescription` e a resposta. */
  makingOffer: boolean;
  /** `true` durante `setRemoteDescription`. */
  settingRemoteAnswerPending: boolean;
  ignoreOffer: boolean;
  /** Senders por sourceId, para trocar e remover track sem renegociar tudo. */
  senders: Map<string, RTCRtpSender>;
  connectionState: RTCPeerConnectionState;
  iceState: RTCIceConnectionState;
  /** Última vez que a conexão esteve `connected`. Base do aviso de instabilidade. */
  lastConnectedAt: number | null;
  /** Tentativas de ICE restart nesta conexão. */
  restarts: number;
}

export interface MeshEvents {
  onRemoteTrack?: (track: RemoteTrack) => void;
  onRemoteTrackEnded?: (peerSessionId: string, trackId: string) => void;
  onPeerStateChange?: (peer: PeerState) => void;
  /** Mensagem recebida pelo DataChannel de controle. */
  onControlMessage?: (peerSessionId: string, message: ControlMessage) => void;
  /** Chamado quando a malha considera um par perdido de forma definitiva. */
  onPeerLost?: (peerSessionId: string, reason: string) => void;
}

/**
 * Mensagens do canal de controle.
 *
 * Este é o protocolo que sobrevive à queda do servidor. É deliberadamente
 * pequeno: só o que precisa funcionar sem o gateway. Presença, histórico e
 * roster continuam sendo do servidor — reimplementá-los aqui seria construir um
 * segundo sistema distribuído dentro do cliente.
 */
export type ControlMessage =
  | { t: 'mute'; muted: boolean }
  | { t: 'deaf'; deafened: boolean }
  | { t: 'sources'; sources: Array<{ sourceId: string; purpose: TrackPurpose; label: string }> }
  | { t: 'ping'; at: number }
  | { t: 'pong'; at: number }
  /** Pedido de renegociação quando o gateway não está disponível. */
  | { t: 'renegotiate' };

export interface MeshOptions {
  /** Nossa sessão de gateway. Base da decisão polite/impolite. */
  selfSessionId: string;
  iceServers: RTCIceServer[];
  transport: SignalTransport;
  events?: MeshEvents;
}

/**
 * Máximo de ICE restarts por par antes de desistir.
 *
 * Reiniciar em laço numa rede que não vai voltar consome CPU e banda e mantém a
 * interface dizendo "conectando" indefinidamente. Três tentativas cobrem troca
 * de rede (Wi-Fi para celular) e queda de rota; além disso, o problema não é
 * transitório e o usuário precisa saber.
 */
const MAX_ICE_RESTARTS = 3;

export class VoiceMesh {
  private readonly peers = new Map<string, PeerState>();
  private readonly localTracks = new Map<string, LocalTrack>();
  private readonly options: MeshOptions;
  private disposed = false;

  constructor(options: MeshOptions) {
    this.options = options;
  }

  get peerList(): PeerState[] {
    return [...this.peers.values()];
  }

  get peerCount(): number {
    return this.peers.size;
  }

  get localTrackList(): LocalTrack[] {
    return [...this.localTracks.values()];
  }

  /* ==========================================================================
     Ciclo de vida dos pares
     ========================================================================== */

  /**
   * Abre a conexão com um par.
   *
   * Só o lado IMPOLITE oferece primeiro. Se os dois oferecessem, a colisão
   * aconteceria em toda entrada de participante — e a negociação perfeita
   * resolveria, mas ao custo de um rollback por conexão. Deixar o impolite
   * começar evita a colisão em vez de tratá-la.
   */
  addPeer(userId: string, sessionId: string): PeerState {
    const existing = this.peers.get(sessionId);
    if (existing) return existing;

    const polite = isPolitePeer(this.options.selfSessionId, sessionId);

    const connection = new RTCPeerConnection({
      iceServers: this.options.iceServers,
      /*
       * `bundlePolicy: 'max-bundle'` multiplexa tudo numa porta.
       *
       * Sem isso, cada track abre seu próprio par de portas ICE — e com duas
       * telas mais áudio mais câmera são quatro negociações de porta por par,
       * multiplicadas por sete pares. Em rede corporativa isso é a diferença
       * entre conectar e não conectar.
       */
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require',
      /*
       * Pool de candidatos zerado de propósito.
       *
       * Pré-coletar candidatos aceleraria o primeiro handshake em alguns
       * milissegundos e, em malha, dispararia coleta para N conexões antes de
       * saber quantas serão usadas — gastando requisições de STUN/TURN que
       * podem não ser necessárias.
       */
      iceCandidatePoolSize: 0,
    });

    const peer: PeerState = {
      userId,
      sessionId,
      polite,
      connection,
      control: null,
      makingOffer: false,
      settingRemoteAnswerPending: false,
      ignoreOffer: false,
      senders: new Map(),
      connectionState: connection.connectionState,
      iceState: connection.iceConnectionState,
      lastConnectedAt: null,
      restarts: 0,
    };

    this.peers.set(sessionId, peer);
    this.wire(peer);

    // O impolite cria o canal de controle: criar dos dois lados abriria dois
    // canais, e cada lado usaria o próprio para enviar — funcionando, mas com o
    // dobro dos recursos e ordem de entrega não garantida entre eles.
    if (!polite) {
      this.openControlChannel(peer);
    }

    // Anexa as tracks locais já existentes. Uma pessoa que entra numa chamada em
    // andamento precisa receber o que já está sendo compartilhado.
    for (const local of this.localTracks.values()) {
      this.attachTrack(peer, local);
    }

    return peer;
  }

  removePeer(sessionId: string, reason = 'saiu'): void {
    const peer = this.peers.get(sessionId);
    if (!peer) return;

    /*
     * Fecha o DataChannel ANTES da conexão.
     *
     * `close()` na RTCPeerConnection não dispara `onclose` no DataChannel em
     * todos os navegadores, e um canal que o código considera aberto mas está
     * morto produz envios silenciosamente perdidos.
     */
    try {
      peer.control?.close();
    } catch {
      // Canal já fechado lança em alguns navegadores. O objetivo era garantir
      // que está fechado, e está.
    }

    for (const sender of peer.senders.values()) {
      try {
        peer.connection.removeTrack(sender);
      } catch {
        // Idem: a conexão pode já estar fechando.
      }
    }

    peer.connection.close();
    this.peers.delete(sessionId);
    this.options.events?.onPeerLost?.(sessionId, reason);
  }

  private wire(peer: PeerState): void {
    const { connection } = peer;

    /* ---- Negociação ---- */
    connection.addEventListener('negotiationneeded', () => {
      void (async () => {
        try {
          peer.makingOffer = true;
          /*
           * `setLocalDescription()` sem argumento.
           *
           * A forma moderna cria a offer OU a answer conforme o estado, e é o
           * que torna a negociação perfeita implementável sem rastrear estado
           * manualmente. Chamar `createOffer()` e depois
           * `setLocalDescription(offer)` abre uma janela entre as duas em que o
           * estado pode mudar — e é exatamente essa janela que causa o glare.
           */
          await connection.setLocalDescription();
          const description = connection.localDescription;
          if (description?.sdp) {
            this.options.transport.signal(peer.userId, peer.sessionId, {
              kind: description.type === 'offer' ? 'offer' : 'answer',
              sdp: description.sdp,
            });
          }
        } catch (error) {
          console.error('[mesh] falha ao negociar', error);
        } finally {
          peer.makingOffer = false;
        }
      })();
    });

    /* ---- ICE ---- */
    connection.addEventListener('icecandidate', (event) => {
      if (event.candidate === null) {
        // `null` significa fim dos candidatos. Avisar o par explicitamente
        // permite que ele pare de esperar em vez de contar com timeout.
        this.options.transport.signal(peer.userId, peer.sessionId, {
          kind: 'ice_complete',
        });
        return;
      }
      this.options.transport.signal(peer.userId, peer.sessionId, {
        kind: 'ice',
        candidate: event.candidate.candidate,
        sdp_mid: event.candidate.sdpMid,
        sdp_m_line_index: event.candidate.sdpMLineIndex,
        username_fragment: event.candidate.usernameFragment,
      });
    });

    connection.addEventListener('iceconnectionstatechange', () => {
      peer.iceState = connection.iceConnectionState;

      /*
       * `disconnected` NÃO dispara restart imediato.
       *
       * Ele é frequentemente transitório — uma troca de rota que se resolve em
       * dois segundos. Reiniciar o ICE nesse momento derrubaria uma conexão que
       * ia voltar sozinha. `failed` é o estado terminal, e é nele que o restart
       * faz sentido.
       */
      if (connection.iceConnectionState === 'failed') {
        this.restartIce(peer);
      }

      this.options.events?.onPeerStateChange?.(peer);
    });

    connection.addEventListener('connectionstatechange', () => {
      peer.connectionState = connection.connectionState;
      if (connection.connectionState === 'connected') {
        peer.lastConnectedAt = Date.now();
        // Reconectou: zera o contador para que uma nova sequência de falhas
        // tenha o orçamento completo de tentativas.
        peer.restarts = 0;
      }
      if (connection.connectionState === 'failed') {
        this.options.events?.onPeerLost?.(peer.sessionId, 'conexão falhou');
      }
      this.options.events?.onPeerStateChange?.(peer);
    });

    /* ---- Mídia recebida ---- */
    connection.addEventListener('track', (event) => {
      const [stream] = event.streams;
      const transceiver = event.transceiver;

      /*
       * O propósito do track vem do `mid` casado com o que o par anunciou pelo
       * canal de controle — não de heurística sobre o track.
       *
       * A tentação é inferir: "kind === 'audio' logo é microfone, e o primeiro
       * vídeo é câmera". Isso quebra com duas telas e nenhuma câmera, que é
       * justamente o caso central deste produto. Sem o anúncio, um
       * compartilhamento de tela apareceria como câmera.
       */
      const purpose = this.inferPurpose(peer, transceiver.mid, event.track);

      const remote: RemoteTrack = {
        track: event.track,
        stream: stream ?? new MediaStream([event.track]),
        purpose,
        peerUserId: peer.userId,
        peerSessionId: peer.sessionId,
        mid: transceiver.mid,
      };

      /*
       * `ended` NÃO é o evento que importa aqui — e essa foi a causa de um
       * defeito visível.
       * ======================================================================
       * Numa faixa REMOTA, `ended` dispara só quando ela é encerrada de forma
       * permanente: `transceiver.stop()`, ou a conexão fechando. Quando o outro
       * lado simplesmente PARA DE ENVIAR — `removeTrack`, `replaceTrack(null)`,
       * ou fechar o compartilhamento pelo botão do navegador — o que chega é
       * `mute`.
       *
       * O sintoma foi exatamente este: quem parava de compartilhar deixava o
       * palco de quem assistia congelado no último quadro. A faixa continuava na
       * lista, o `<video>` continuava montado com um track mudo, e um track de
       * vídeo mudo não pinta nada novo — mantém o último frame na tela. Não
       * havia como sair a não ser trocar de canal.
       *
       * `mute` é tratado como saída e `unmute` como volta. A faixa não é
       * destruída: a mesma faixa volta a receber quadros se a pessoa retomar, e
       * é por isso que `unmute` reemite o mesmo objeto em vez de esperar um
       * `ontrack` novo — que não viria, porque o transceiver nunca mudou.
       */
      if (event.track.kind === 'video') {
        event.track.addEventListener('mute', () => {
          this.options.events?.onRemoteTrackEnded?.(peer.sessionId, event.track.id);
        });
        event.track.addEventListener('unmute', () => {
          this.options.events?.onRemoteTrack?.(remote);
        });
      }

      /*
       * Em ÁUDIO, `mute` é ignorado de propósito.
       *
       * Ele dispara quando o par fecha o microfone — o caso mais comum de todos.
       * Remover a faixa nessa hora destruiria o elemento de áudio, e ao
       * desmutar o som só voltaria depois de uma renegociação. Silêncio numa
       * faixa viva é exatamente o que se quer: o elemento continua montado e não
       * toca nada.
       */
      event.track.addEventListener('ended', () => {
        this.options.events?.onRemoteTrackEnded?.(peer.sessionId, event.track.id);
      });

      /*
       * Faixa que JÁ chega muda não é anunciada como presente.
       *
       * Acontece de verdade: a oferta pode criar o transceiver antes de haver
       * conteúdo. Emitir aqui colocaria um quadro preto na grade até o primeiro
       * frame chegar.
       */
      if (event.track.kind === 'video' && event.track.muted) {
        return;
      }

      this.options.events?.onRemoteTrack?.(remote);
    });

    /* ---- Canal de controle criado pelo outro lado ---- */
    connection.addEventListener('datachannel', (event) => {
      if (event.channel.label !== 'ctrl') {
        // Canal desconhecido é fechado, não ignorado. Um canal aberto que
        // ninguém lê é um vetor de consumo de memória.
        event.channel.close();
        return;
      }
      this.bindControlChannel(peer, event.channel);
    });
  }

  /**
   * Deduz o propósito de um track remoto.
   *
   * Prioridade: o anúncio do par pelo canal de controle, casado por `mid`. Sem
   * anúncio, cai em `microphone` para áudio e `camera` para vídeo — que é a
   * suposição menos danosa: exibir uma tela como se fosse câmera é confuso, e
   * exibir câmera como tela também, mas o segundo caso só ocorre antes do
   * anúncio chegar, e o anúncio corrige.
   */
  private readonly announcedSources = new Map<
    string,
    Map<string, { purpose: TrackPurpose; label: string }>
  >();

  private inferPurpose(
    peer: PeerState,
    mid: string | null,
    track: MediaStreamTrack,
  ): TrackPurpose {
    if (track.kind === 'audio') return 'microphone';

    const announced = this.announcedSources.get(peer.sessionId);
    if (announced && mid !== null) {
      const match = announced.get(mid);
      if (match) return match.purpose;
    }
    return 'camera';
  }

  /* ==========================================================================
     Sinalização recebida
     ========================================================================== */

  /**
   * Processa sinalização de um par. É aqui que a negociação perfeita vive.
   */
  async handleSignal(
    fromUserId: string,
    fromSessionId: string,
    payload:
      | { kind: 'offer'; sdp: string }
      | { kind: 'answer'; sdp: string }
      | {
          kind: 'ice';
          candidate: string;
          sdp_mid: string | null;
          sdp_m_line_index: number | null;
          username_fragment?: string | null;
        }
      | { kind: 'ice_complete' }
      | { kind: 'restart' },
  ): Promise<void> {
    if (this.disposed) return;

    // Sinalização de um par desconhecido CRIA o par. É o caminho normal para
    // quem entra na sala depois: o roster do servidor e a primeira offer podem
    // chegar em qualquer ordem.
    const peer = this.peers.get(fromSessionId) ?? this.addPeer(fromUserId, fromSessionId);
    const { connection } = peer;

    try {
      if (payload.kind === 'offer' || payload.kind === 'answer') {
        const description: RTCSessionDescriptionInit = {
          type: payload.kind,
          sdp: payload.sdp,
        };

        /*
         * O coração da negociação perfeita.
         *
         * `readyToReceiveOffer` é verdadeiro quando não há offer nossa em voo.
         * Se há colisão:
         *   - polite   -> aceita a do outro (o rollback é implícito no
         *                 setRemoteDescription moderno) e descarta a própria
         *   - impolite -> IGNORA a do outro e mantém a própria
         *
         * Sem esta lógica, uma sala de 8 pessoas onde todos entram ao mesmo
         * tempo trava em `have-local-offer` de forma reproduzível.
         */
        const offerCollision =
          payload.kind === 'offer' &&
          (peer.makingOffer || connection.signalingState !== 'stable');

        peer.ignoreOffer = !peer.polite && offerCollision;
        if (peer.ignoreOffer) return;

        peer.settingRemoteAnswerPending = payload.kind === 'answer';
        await connection.setRemoteDescription(description);
        peer.settingRemoteAnswerPending = false;

        if (payload.kind === 'offer') {
          await connection.setLocalDescription();
          const answer = connection.localDescription;
          if (answer?.sdp) {
            this.options.transport.signal(peer.userId, peer.sessionId, {
              kind: 'answer',
              sdp: answer.sdp,
            });
          }
        }
        return;
      }

      if (payload.kind === 'ice') {
        try {
          await connection.addIceCandidate({
            candidate: payload.candidate,
            sdpMid: payload.sdp_mid,
            sdpMLineIndex: payload.sdp_m_line_index,
            ...(payload.username_fragment != null
              ? { usernameFragment: payload.username_fragment }
              : {}),
          });
        } catch (error) {
          /*
           * Candidato rejeitado é ENGOLIDO quando estamos ignorando a offer.
           *
           * O par impolite que descartou uma offer vai receber os candidatos
           * dela, e eles não casam com nenhuma descrição — o erro é esperado e
           * não indica problema. Logar isso como erro encheria o console
           * durante toda entrada de participante.
           */
          if (!peer.ignoreOffer) {
            console.warn('[mesh] candidato ICE recusado', error);
          }
        }
        return;
      }

      if (payload.kind === 'restart') {
        this.restartIce(peer);
        return;
      }

      // `ice_complete` não exige ação: a implementação do navegador já lida com
      // o fim da coleta. O evento existe para diagnóstico e para o par saber
      // que não vem mais nada.
    } catch (error) {
      console.error('[mesh] falha ao processar sinalização', error);
    }
  }

  /**
   * Reinicia o ICE de um par.
   *
   * Só o IMPOLITE reinicia. Se os dois reiniciassem, cada restart geraria uma
   * offer nova e a colisão seria contínua — a negociação perfeita resolveria
   * cada uma, mas a conexão nunca estabilizaria. O polite PEDE, por sinalização,
   * e deixa o impolite executar.
   */
  private restartIce(peer: PeerState): void {
    if (peer.restarts >= MAX_ICE_RESTARTS) {
      this.options.events?.onPeerLost?.(
        peer.sessionId,
        `ICE falhou após ${MAX_ICE_RESTARTS} tentativas`,
      );
      return;
    }
    peer.restarts += 1;

    if (peer.polite) {
      this.options.transport.signal(peer.userId, peer.sessionId, { kind: 'restart' });
      return;
    }

    try {
      peer.connection.restartIce();
    } catch (error) {
      console.error('[mesh] restartIce falhou', error);
    }
  }

  /* ==========================================================================
     Canal de controle
     ========================================================================== */

  private openControlChannel(peer: PeerState): void {
    /*
     * `ordered: true` e sem `maxRetransmits`: entrega confiável e ordenada.
     *
     * O canal carrega mudança de estado (mute, telas ativas). Perder uma
     * mensagem de mute deixaria o par mostrando o estado errado
     * indefinidamente, porque não há reenvio periódico. Confiabilidade aqui
     * custa latência que não importa — não é mídia.
     */
    const channel = peer.connection.createDataChannel('ctrl', {
      ordered: true,
      protocol: 'concord-ctrl-v1',
    });
    this.bindControlChannel(peer, channel);
  }

  private bindControlChannel(peer: PeerState, channel: RTCDataChannel): void {
    peer.control = channel;

    channel.addEventListener('open', () => {
      // Ao abrir, anuncia as fontes locais. É o que permite ao par distinguir
      // tela de câmera — ver `inferPurpose`.
      this.announceSources(peer);
    });

    channel.addEventListener('message', (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      let message: ControlMessage;
      try {
        message = JSON.parse(event.data) as ControlMessage;
      } catch {
        return;
      }

      if (message.t === 'sources') {
        /*
         * Guarda o anúncio indexado por sourceId, e o casamento com `mid`
         * acontece na ordem de chegada dos tracks.
         *
         * A limitação assumida: com duas telas, distinguir qual `mid` é qual
         * tela depende da ordem, que a especificação não garante. O rótulo
         * chega correto; a associação exata tela↔mid é aproximada. Resolver de
         * forma exata exigiria carregar o sourceId no próprio SDP, o que
         * significaria reescrever SDP — e reescrever SDP é frágil entre versões
         * de navegador. Registrado como limite conhecido.
         */
        const byMid = new Map<string, { purpose: TrackPurpose; label: string }>();
        const transceivers = peer.connection
          .getTransceivers()
          .filter((t) => t.receiver.track?.kind === 'video' && t.mid !== null);

        message.sources
          .filter((s) => s.purpose !== 'microphone')
          .forEach((source, index) => {
            const mid = transceivers[index]?.mid;
            if (mid) byMid.set(mid, { purpose: source.purpose, label: source.label });
          });

        this.announcedSources.set(peer.sessionId, byMid);

        /*
         * O anúncio RECALCULA o propósito das faixas já entregues.
         *
         * O comentário de `inferPurpose` prometia que "o anúncio corrige", e
         * nada corrigia: `purpose` era gravado uma vez, no `ontrack`, num campo
         * comum do `RemoteTrack`. Como o anúncio chega pelo canal de controle
         * DEPOIS de a faixa aparecer, toda tela compartilhada era entregue como
         * `camera` e ficava assim — o palco a rotulava "Câmera" e a colocava num
         * card de participante em vez da faixa de telas.
         *
         * Reemitir com o propósito certo é o caminho mais simples: o consumidor
         * já substitui a faixa por id (`filtered` no motor), então uma segunda
         * emissão da mesma faixa é uma atualização, não uma duplicata.
         */
        for (const transceiver of peer.connection.getTransceivers()) {
          const track = transceiver.receiver.track;
          if (!track || track.kind !== 'video' || transceiver.mid === null) continue;

          const announced = byMid.get(transceiver.mid);
          if (!announced) continue;

          // Muda nada se já estava certo: evita re-render por nada.
          if (track.muted) continue;

          this.options.events?.onRemoteTrack?.({
            track,
            stream: new MediaStream([track]),
            purpose: announced.purpose,
            peerUserId: peer.userId,
            peerSessionId: peer.sessionId,
            mid: transceiver.mid,
          });
        }
      }

      if (message.t === 'ping') {
        this.sendControl(peer.sessionId, { t: 'pong', at: message.at });
        return;
      }

      this.options.events?.onControlMessage?.(peer.sessionId, message);
    });

    channel.addEventListener('close', () => {
      if (peer.control === channel) peer.control = null;
    });
  }

  private announceSources(peer: PeerState): void {
    this.sendControl(peer.sessionId, {
      t: 'sources',
      sources: [...this.localTracks.values()].map((local) => ({
        sourceId: local.sourceId,
        purpose: local.purpose,
        label: local.label,
      })),
    });
  }

  /** Envia pelo canal de controle. `false` se o canal não está aberto. */
  sendControl(peerSessionId: string, message: ControlMessage): boolean {
    const peer = this.peers.get(peerSessionId);
    if (peer?.control?.readyState !== 'open') return false;
    peer.control.send(JSON.stringify(message));
    return true;
  }

  /** Envia para todos os pares. Devolve quantos receberam. */
  broadcastControl(message: ControlMessage): number {
    let delivered = 0;
    for (const peer of this.peers.values()) {
      if (this.sendControl(peer.sessionId, message)) delivered += 1;
    }
    return delivered;
  }

  /* ==========================================================================
     Mídia local
     ========================================================================== */

  /** Adiciona um track local a TODOS os pares. */
  addLocalTrack(local: LocalTrack): void {
    this.localTracks.set(local.sourceId, local);
    for (const peer of this.peers.values()) {
      this.attachTrack(peer, local);
    }
    // Reanuncia: o par precisa saber que existe uma tela nova antes de o track
    // chegar, senão ele o classifica como câmera.
    for (const peer of this.peers.values()) this.announceSources(peer);
  }

  /** Remove um track local de todos os pares. */
  removeLocalTrack(sourceId: string): void {
    const local = this.localTracks.get(sourceId);
    if (!local) return;
    this.localTracks.delete(sourceId);

    for (const peer of this.peers.values()) {
      const sender = peer.senders.get(sourceId);
      if (!sender) continue;
      try {
        peer.connection.removeTrack(sender);
      } catch (error) {
        console.warn('[mesh] removeTrack falhou', error);
      }
      peer.senders.delete(sourceId);
    }

    for (const peer of this.peers.values()) this.announceSources(peer);
  }

  /**
   * Substitui o track de uma fonte sem renegociar.
   *
   * `replaceTrack` é o que permite trocar de microfone no meio da chamada sem
   * um round-trip de SDP por par. Renegociar com sete pares para trocar de
   * dispositivo produziria um corte audível de vários segundos.
   */
  async replaceLocalTrack(sourceId: string, track: MediaStreamTrack): Promise<void> {
    const local = this.localTracks.get(sourceId);
    if (!local) return;
    this.localTracks.set(sourceId, { ...local, track });

    await Promise.all(
      [...this.peers.values()].map(async (peer) => {
        const sender = peer.senders.get(sourceId);
        if (!sender) return;
        try {
          await sender.replaceTrack(track);
        } catch (error) {
          console.error('[mesh] replaceTrack falhou', error);
        }
      }),
    );
  }

  private attachTrack(peer: PeerState, local: LocalTrack): void {
    if (peer.senders.has(local.sourceId)) return;
    try {
      /*
       * `addTransceiver` com `sendonly` em vez de `addTrack`.
       *
       * `addTrack` cria um transceiver bidirecional, e o `sendrecv` faz o
       * navegador reservar capacidade de RECEBER no mesmo m-line. Numa malha, o
       * recebimento vem de transceivers criados pela offer do outro lado — o
       * `recv` daqui fica sobrando e infla o SDP, que com 2 telas × 7 pares
       * chega perto do limite de frame do gateway.
       */
      const transceiver = peer.connection.addTransceiver(local.track, {
        direction: 'sendonly',
        streams: [new MediaStream([local.track])],
      });
      peer.senders.set(local.sourceId, transceiver.sender);

      /*
       * `contentHint` é o ajuste de maior efeito por linha de código.
       *
       * `detail` diz ao codificador para preservar NITIDEZ em vez de fluidez —
       * é o que faz texto de IDE ficar legível numa tela compartilhada.
       * `motion` faz o oposto, para vídeo e jogo. Sem definir, o navegador
       * escolhe por conta e o resultado comum é texto ilegível.
       */
      if (local.purpose === 'screen') {
        local.track.contentHint = 'detail';
      } else if (local.purpose === 'camera') {
        local.track.contentHint = 'motion';
      }
    } catch (error) {
      console.error('[mesh] addTransceiver falhou', error);
    }
  }

  /** Habilita ou desabilita o áudio local em todos os pares. */
  setMicrophoneEnabled(enabled: boolean): void {
    for (const local of this.localTracks.values()) {
      if (local.purpose === 'microphone') local.track.enabled = enabled;
    }
    /*
     * `track.enabled = false` continua ENVIANDO pacotes, só que silenciosos.
     *
     * É o comportamento certo para mute: parar de enviar derrubaria a
     * estimativa de banda e o par teria de reconstruí-la ao desmutar,
     * produzindo os primeiros segundos com qualidade ruim. O anúncio pelo canal
     * de controle é o que faz o ícone de mudo aparecer do outro lado.
     */
    this.broadcastControl({ t: 'mute', muted: !enabled });
  }

  /* ==========================================================================
     Estatísticas
     ========================================================================== */

  /**
   * Coleta estatísticas de todos os pares, no formato que a telemetria envia.
   *
   * `getStats()` devolve um grafo grande com dezenas de tipos de relatório. Este
   * método extrai só o que responde "por que a chamada está ruim": RTT, jitter,
   * perda, bitrate e o motivo de limitação de qualidade reportado pelo próprio
   * codificador — que é o campo que distingue "sua CPU não dá conta" de "sua
   * banda não dá conta".
   */
  async collectStats(): Promise<
    Array<{
      peerUserId: string;
      peerSessionId: string;
      transport: 'host' | 'srflx' | 'prflx' | 'relay' | null;
      trackKind: 'audio' | 'camera' | 'screen' | null;
      rttMs: number | null;
      jitterMs: number | null;
      packetLossPct: number | null;
      outboundKbps: number | null;
      inboundKbps: number | null;
      frameWidth: number | null;
      frameHeight: number | null;
      fps: number | null;
      qualityLimitation: string | null;
    }>
  > {
    const samples: Awaited<ReturnType<VoiceMesh['collectStats']>> = [];

    for (const peer of this.peers.values()) {
      let report: RTCStatsReport;
      try {
        report = await peer.connection.getStats();
      } catch {
        continue;
      }

      let transport: 'host' | 'srflx' | 'prflx' | 'relay' | null = null;
      let rttMs: number | null = null;

      // Primeiro passo: encontra o par de candidatos EM USO. É ele que diz se a
      // mídia está indo direto ou por relay — a diferença entre custo zero e
      // banda do servidor.
      for (const entry of report.values()) {
        if (entry.type === 'candidate-pair' && entry.state === 'succeeded' && entry.nominated) {
          const local = report.get(entry.localCandidateId) as
            | { candidateType?: string }
            | undefined;
          const type = local?.candidateType;
          if (type === 'host' || type === 'srflx' || type === 'prflx' || type === 'relay') {
            transport = type;
          }
          if (typeof entry.currentRoundTripTime === 'number') {
            rttMs = Math.round(entry.currentRoundTripTime * 1000);
          }
        }
      }

      for (const entry of report.values()) {
        if (entry.type === 'outbound-rtp') {
          const kind: 'audio' | 'camera' | 'screen' | null =
            entry.kind === 'audio'
              ? 'audio'
              : entry.mid !== undefined
                ? this.trackKindForMid(entry.mid as string)
                : 'camera';

          samples.push({
            peerUserId: peer.userId,
            peerSessionId: peer.sessionId,
            transport,
            trackKind: kind,
            rttMs,
            jitterMs: null,
            packetLossPct: null,
            outboundKbps:
              typeof entry.bytesSent === 'number'
                ? Math.round((entry.bytesSent * 8) / 1000)
                : null,
            inboundKbps: null,
            frameWidth: (entry.frameWidth as number | undefined) ?? null,
            frameHeight: (entry.frameHeight as number | undefined) ?? null,
            fps: (entry.framesPerSecond as number | undefined) ?? null,
            qualityLimitation:
              (entry.qualityLimitationReason as string | undefined) ?? null,
          });
        }

        if (entry.type === 'inbound-rtp') {
          const lost = entry.packetsLost as number | undefined;
          const received = entry.packetsReceived as number | undefined;
          const total = (lost ?? 0) + (received ?? 0);

          samples.push({
            peerUserId: peer.userId,
            peerSessionId: peer.sessionId,
            transport,
            trackKind: entry.kind === 'audio' ? 'audio' : 'camera',
            rttMs,
            jitterMs:
              typeof entry.jitter === 'number' ? Math.round(entry.jitter * 1000) : null,
            // Percentual e não contagem: 500 pacotes perdidos não significa nada
            // sem saber de quantos. A razão é o que se compara entre chamadas.
            packetLossPct:
              total > 0 && lost !== undefined
                ? Math.round((lost / total) * 10_000) / 100
                : null,
            outboundKbps: null,
            inboundKbps:
              typeof entry.bytesReceived === 'number'
                ? Math.round((entry.bytesReceived * 8) / 1000)
                : null,
            frameWidth: (entry.frameWidth as number | undefined) ?? null,
            frameHeight: (entry.frameHeight as number | undefined) ?? null,
            fps: (entry.framesPerSecond as number | undefined) ?? null,
            qualityLimitation: null,
          });
        }
      }
    }

    return samples;
  }

  private trackKindForMid(mid: string): 'camera' | 'screen' {
    for (const [sourceId, local] of this.localTracks) {
      for (const peer of this.peers.values()) {
        const sender = peer.senders.get(sourceId);
        const transceiver = peer.connection
          .getTransceivers()
          .find((t) => t.sender === sender);
        if (transceiver?.mid === mid) {
          return local.purpose === 'screen' ? 'screen' : 'camera';
        }
      }
    }
    return 'camera';
  }

  /** Fecha tudo. */
  dispose(): void {
    this.disposed = true;
    for (const sessionId of [...this.peers.keys()]) {
      this.removePeer(sessionId, 'malha encerrada');
    }
    this.localTracks.clear();
    this.announcedSources.clear();
  }
}
